import { AfterViewInit, Component, OnDestroy, OnInit, EventEmitter } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CurrentUser } from "../../../common/providers/current-user";
import { PVC_TEST_ID, TestLeftMenu, TestProvider } from '../../providers/test-provider';
import { LangsProvider } from "../../../common/providers/langs-provider";
import { forkJoin, from, Observable, of, Subscription, tap } from 'rxjs';
import { BreadcrumbProvider, Crumb } from '../../../breadcrumb/providers/breadcrumb-provider';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UsersProvider } from '../../../users/providers/users-provider';
import { mapTestItemRightsToRowRights, TestRights } from '../../providers/test-rights';
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import { ConfigProvider } from "../../../config-provider.service";
import { Title } from '@angular/platform-browser';
import { Organization } from "../../../common/models/organization";
import { Athlete } from "../../../common/models/athlete";
import { User } from "../../../common/models/user";
import { Test } from "../../../common/models/test";
import { OrganizationRole } from "../../../common/models/organization-role";
import { catchError, finalize, map } from "rxjs/operators";
import { PageContentComponent } from "../../../common/components/page-content/page-content";
import { HeaderComponent } from "../../../common/components/header-elitment/header-elitment";
import { SideMenuComponent } from "../../../common/components/side-menu/side-menu";
import { TestSideMenuComponent } from "../../components/side-menu/side-menu";
import { Error401Component } from "../../../common/components/error-401/error-401";
import { Error404Component } from "../../../common/components/error-404/error-404";
import { AsyncPipe, NgIf } from "@angular/common";
import { ModuleContentComponent } from "../../../common/components/module-content/module-content";
import { TestMultiLineBorgChartComponent } from "../../components/multi-line-borg-chart/test-multi-line-borg-chart";

@Component({
  selector: 'app-all-test-container',
  standalone: true,
  templateUrl: './all-test-container.component.html',
  styleUrls: ['./all-test-container.scss'],
  imports: [
    PageContentComponent,
    HeaderComponent,
    SideMenuComponent,
    TestSideMenuComponent,
    Error401Component,
    Error404Component,
    NgIf,
    ModuleContentComponent,
    TranslateModule,
    TestMultiLineBorgChartComponent,
    AsyncPipe
  ]
})
export class AllTestContainerComponent implements OnInit, AfterViewInit, OnDestroy {

    public pageTitle = 'Tests';
    public isLoading = true;
    public is401 = false;
    public is404 = false;

    public graphClass = 'test-multi-line-chart';

    public organization: Organization = {} as Organization;
    public athleteUser: User = {} as User;
    public tests: Test[] = [];
    public testsLeftMenu: TestLeftMenu[] = [];
    public measures: any;
    public testsChange = new EventEmitter<void>();

    public rights: TestRights = new TestRights(this.utils);
    private subscriptions: Subscription[] = [];

    constructor(
        public conf: ConfigProvider,
        private title: Title,
        private utils: UtilsProvider,
        private currentUser: CurrentUser,
        private currentUserRoles: CurrentUserRolesProvider,
        private route: ActivatedRoute,
        private testProvider: TestProvider,
        private langsProvider: LangsProvider,
        private breadcrumbProvider: BreadcrumbProvider,
        private userProvider: UsersProvider,
        private translator: TranslateService,
    ) {
    }

    public ngOnInit(): void {
        // Load athlete and annual plan if it exists
        this.subscriptions.push(
            this.route.data.subscribe((data: any) => { // { organization: Organization; athlete: Athlete; test: Test }
                if (data.organization) {
                    this.organization = data.organization;
                }

                if (data.athlete) {
                    this.athleteUser = data.athlete;
                }

                this.loadData();
            })
        );
        this.subscriptions.push(
            this.testsChange.subscribe(() => {
                this.updateSideMenuEvent();
            })
        );
    }

    public ngAfterViewInit(): void {

    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

  public loadData(): void {
    const pvcTest = { id: PVC_TEST_ID } as Test;
    forkJoin([
      this.testProvider.findAllUniqueWithMaxDate(this.organization, this.athleteUser, this.langsProvider.getBestLangId()),
      this.testProvider.getAllMeasures(this.organization, this.athleteUser.athlete!, { params: { lang: this.langsProvider.getBestLangId() } }),
      this.testProvider.getMeasures(this.organization, this.athleteUser.athlete!, pvcTest).pipe(
        catchError(() => of([]))
      ),
    ]).pipe(
      map(([testsLeftMenu, measures, pvcMeasures]: [TestLeftMenu[], any, any[]]) => {
        this.testsLeftMenu = testsLeftMenu;
        this.measures = measures;

        const pvcArray = Array.isArray(pvcMeasures) ? pvcMeasures : [];
        if (pvcArray.length > 0) {
          if (!this.measures.series) { this.measures.series = {}; }
          if (!this.measures.series.borg) { this.measures.series.borg = {}; }
          this.measures.series.borg['PVC'] = {
            name: 'PVC',
            data: pvcArray
              .map((item: any) => [new Date(item.date).getTime(), item.axis_y])
              .sort((a: number[], b: number[]) => a[0] - b[0]),
          };
        }
      })
    ).subscribe({
      next: () => {
        this.rights.evaluateScreenRights({
          rolesIds: this.currentUserRoles.getRolesIds(this.organization.id!)
        });
        if (!this.rights.LIST) {
          this.is401 = true;
        }
        this.createPageTitle();
        this.createBreadcrumb();
        this.isLoading = false;
      }
    })
  }


    public updateSideMenuEvent() {
        this.isLoading = true;
        this.loadData();
    }

    private checkRights (test: any) {
        let rights = {
            edit: false,
            delete: false,
        }
        if (test.read_only)
            return rights;
        if (this.currentUserRoles.have(OrganizationRole.ID_COACH, this.organization.id!)) {
            rights = {
                edit: test.creater_id === this.currentUser.user!.id,
                delete: false,
            }
        }
        if (this.currentUserRoles.have(OrganizationRole.ID_SUPERCOACH, this.organization.id!)) {
            rights = {
                edit: true,
                delete: true,
            }
        }
        if (this.currentUserRoles.have(OrganizationRole.ID_ADMIN, this.organization.id!)) {
            rights = {
                edit: true,
                delete: true,
            }
        }
        return rights;
    }

    private createPageTitle(){
        this.pageTitle = this.translator.instant('TestsAll');
        this.title.setTitle(this.pageTitle + ' | ' + this.conf.platformName);
    }

    private createBreadcrumb() {
        const crumbs: Crumb[] = [];
        if (!this.currentUserRoles.have(OrganizationRole.ID_ATHLETE, this.organization.id!)){
            crumbs.push({
                shortTitle: 'Athletes',
                title: 'Athletes',
                url: '/' + this.organization!.code + '/athletes'
            });
        }
        crumbs.push({
            shortTitle: `${this.athleteUser.firstName?.substr(0, 1)}. ${this.athleteUser.lastName}`,
            title: this.athleteUser.firstName + ' ' + this.athleteUser.lastName,
            url: `/${this.organization!.code}/athletes/${this.athleteUser.id}`
        });
        crumbs.push({
            shortTitle: 'TestsAll',
            title: 'TestsAll',
        });
        this.breadcrumbProvider.setDefaultCrumbs(crumbs);
    }

}
